import { useRef, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { UsePhoneValidationProps, UsePhoneValidationReturn } from './types'
import { useDebounceCallBack } from '../useDebounce'

import { PhoneData } from '@/types'
import { cleanPhoneNumber } from '@/utils'
import { phoneNumberUtils } from '@/utils/phoneNumber/phoneNumberUtils'

const { validatePhoneNumber } = phoneNumberUtils

export const usePhoneValidation = ({
  phone,
  options = {},
}: UsePhoneValidationProps): UsePhoneValidationReturn => {
  const {
    debounceDelay = 600,
    onValidationChange,
    autoValidate = true,
  } = options

  const [phoneIsValid, setPhoneIsValid] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const lastValidNumber = useRef('')

  const { t } = useTranslation()

  const completePhone = cleanPhoneNumber(
    `${phone?.countryCode}${phone?.number}`,
  )
  const invalidPhoneErrorMessage = t('api:errors.invalidPhone')

  const validatePhone = useCallback(
    (phoneToValidate: PhoneData) => {
      if (!phoneToValidate?.number || !phoneToValidate?.countryIsoCode) {
        setPhoneIsValid(false)
        setErrorMessage('')
        onValidationChange?.(false, '')
        return
      }

      const currentCompleteNumber = cleanPhoneNumber(
        `${phoneToValidate.countryCode}${phoneToValidate.number}`,
      )

      const { isValid } = validatePhoneNumber(
        currentCompleteNumber,
        phoneToValidate.countryIsoCode,
      )

      if (!isValid) {
        lastValidNumber.current = ''
        setPhoneIsValid(false)
        setErrorMessage(invalidPhoneErrorMessage)
        onValidationChange?.(false, invalidPhoneErrorMessage)
      } else {
        lastValidNumber.current = currentCompleteNumber
        setPhoneIsValid(true)
        setErrorMessage('')
        onValidationChange?.(true, '')
      }
    },
    [invalidPhoneErrorMessage, onValidationChange],
  )

  const resetValidation = useCallback(() => {
    setPhoneIsValid(false)
    setErrorMessage('')
    lastValidNumber.current = ''
    onValidationChange?.(false, '')
  }, [onValidationChange])

  // Use useDebounceCallBack for auto-validation
  const debouncedValidatePhone = useDebounceCallBack(
    validatePhone,
    debounceDelay,
  )

  // Auto-validate when phone changes
  useEffect(() => {
    if (!autoValidate || !phone?.number || !phone?.countryIsoCode) {
      return
    }

    // Mark phone as invalid again until revalidated
    if (phoneIsValid && completePhone !== lastValidNumber.current) {
      setPhoneIsValid(false)
      setErrorMessage('')
      onValidationChange?.(false, '')
    }

    debouncedValidatePhone(phone)
  }, [
    phone,
    autoValidate,
    debouncedValidatePhone,
    phoneIsValid,
    completePhone,
    onValidationChange,
  ])

  return {
    phoneIsValid,
    errorMessage,
    lastValidNumber,
    validatePhone,
    resetValidation,
    setPhoneIsValid,
    setErrorMessage,
    completePhone,
  }
}
